import {
  Box,
  Hidden,
  IconArrow,
  Stack,
  useResponsiveValue,
} from 'braid-design-system';
import {
  type ComponentProps,
  type ForwardedRef,
  forwardRef,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { matchPath, useLocation } from 'react-router';

import { ConditionalWrapper } from '#src/components/ConditionalWrapper/ConditionalWrapper.tsx';
import {
  FunctionalMenuItem,
  MenuItem,
} from '#src/components/MenuItem/MenuItem.tsx';
import { MenuSection } from '#src/components/MenuSection/MenuSection.tsx';
import { site as dashboardSite } from '#src/dashboard.config.ts';
import { site as documentationSite } from '#src/documentation.config.tsx';
import { usePermissions } from '#src/hooks/auth.tsx';
import type { Page, PageGroup } from '#src/page.ts';
import { SchemaMenu } from '#src/pages/schema/SchemaMenu/SchemaMenu.tsx';

import { useAuth0 } from '@auth0/auth0-react';
import {
  type Breadcrumb,
  Breadcrumbs,
} from '#src/components/Breadcrumbs/Breadcrumbs.tsx';
import { SiteSearch } from '#src/components/SiteSearch/SiteSearch.tsx';
import * as styles from './Menu.css';
import { useOnEscape } from '#src/hooks/useOnEscape.ts';

interface OnClickProps {
  onClick?: () => void;
}

type NavigationLevel = NonNullable<
  Pick<ComponentProps<typeof MenuItem>, 'level'>['level']
>;

const isNavigationLevel = (num: unknown): num is NavigationLevel =>
  typeof num === 'number' && Number.isSafeInteger(num) && num > 0 && num <= 6;

const subtreeIsActive = (root: Page, activePath: string): boolean =>
  Boolean(root.path && matchPath(activePath, root.path)) ||
  Boolean(root.children?.some((child) => subtreeIsActive(child, activePath)));

const renderNestedNavigationForPages = (
  pages: Page[],
  activePath: string,
  ref: ForwardedRef<HTMLAnchorElement>,
  onClick: OnClickProps['onClick'],
  permissions: string[] = [],
  level: number,
) => {
  if (!isNavigationLevel(level)) {
    throw new Error(`${level} is too deep a navigation level`);
  }

  return pages.map((page, index) => (
    <ItemHierarchy
      activePath={activePath}
      key={`${page.path}-${index}`}
      level={level}
      onClick={onClick}
      page={page}
      permissions={permissions}
      ref={ref}
    />
  ));
};

const renderMenuSections = (
  pages: PageGroup[],
  activePath: string,
  ref: ForwardedRef<HTMLAnchorElement>,
  onClick: OnClickProps['onClick'],
  permissions: string[] = [],
) =>
  pages.map((page, i) => {
    const HeadingComponent = page.reactLabel;
    return page.children === undefined ||
      page.children.every((p) => p.hidden) ? undefined : (
      <MenuSection
        key={page.path ?? i}
        heading={HeadingComponent ? <HeadingComponent /> : page.label}
      >
        {page.children === undefined
          ? undefined
          : renderNestedNavigationForPages(
              page.children,
              activePath,
              ref,
              onClick,
              permissions,
              1,
            )}
      </MenuSection>
    );
  });

const TopLevelNavigation = ({
  onSelect,
}: {
  onSelect: (section: 'docs' | 'schema' | 'dashboard') => void;
}) => (
  <MenuSection heading={null}>
    <FunctionalMenuItem onClick={() => onSelect('docs')}>
      Docs
    </FunctionalMenuItem>
    <FunctionalMenuItem onClick={() => onSelect('dashboard')}>
      Dashboard
    </FunctionalMenuItem>
    <FunctionalMenuItem onClick={() => onSelect('schema')}>
      Schema
    </FunctionalMenuItem>
  </MenuSection>
);

type ItemHierarchyProps = {
  activePath: string;
  level: NavigationLevel;
  onClick: OnClickProps['onClick'];
  page: Page;
  permissions: string[];
};

const ItemHierarchy = forwardRef<HTMLAnchorElement, ItemHierarchyProps>(
  ({ activePath, level, onClick, page, permissions }, ref) => {
    const [isUserExpanded, setUserExpanded] = useState<boolean | undefined>(
      page.menuItemStartExpanded,
    );
    const isActive = subtreeIsActive(page, activePath);

    // Default to tracking the active page without explicit user action
    const isExpanded = isUserExpanded ?? isActive;

    const matchingRef =
      page.path && matchPath(activePath, page.path) ? ref : null;

    const itemProps = {
      icon: page.icon,
      isExpanded,
      level,
      onClick,
      setUserExpanded,
      to: page.path,
    };

    if (page.hidden && !isActive) {
      return null;
    }

    const LabelComponent = page.reactLabel;

    if (!page.children?.length) {
      const WrapperComponent = page.menuItemWrapper;
      return (
        <ConditionalWrapper
          wrapper={
            WrapperComponent
              ? (childNode) => <WrapperComponent>{childNode}</WrapperComponent>
              : undefined
          }
          condition={WrapperComponent !== undefined}
        >
          <MenuItem
            {...itemProps}
            collapsible={false}
            // Scroll the leaf item into view
            ref={matchingRef}
          >
            {LabelComponent ? <LabelComponent /> : page.label}
          </MenuItem>
        </ConditionalWrapper>
      );
    }

    return (
      <>
        <MenuItem {...itemProps} collapsible={true}>
          {LabelComponent ? <LabelComponent /> : page.label}
        </MenuItem>

        <Box
          className={styles.menuSection[`${isExpanded}`]}
          // Scroll the expanded children of the item into view
          ref={matchingRef}
        >
          <Box className={styles.menuSectionInner}>
            {renderNestedNavigationForPages(
              page.children,
              activePath,
              ref,
              onClick,
              permissions,
              level + 1,
            )}
          </Box>
        </Box>
      </>
    );
  },
);

type NavigationState =
  | { state: 'default' }
  | { state: 'top-level' }
  | { state: 'chose-section'; section: 'docs' | 'schema' | 'dashboard' };

const useNavigationState = (isOpen: boolean) => {
  const [navigationState, setNavigationState] = useState<NavigationState>({
    state: 'default',
  });

  useEffect(() => {
    setNavigationState({ state: 'default' });
  }, [isOpen]);

  return [navigationState, setNavigationState] as const;
};

const useScrollRefIntoViewOnPathChange = (
  ref: RefObject<HTMLAnchorElement | null>,
  pathname: string,
) => {
  useEffect(
    () => {
      const timeout = setTimeout(() => {
        ref.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      });

      return () => clearTimeout(timeout);
    },

    // Re-scroll on client-side navigation. This may be useful when following an
    // internal link to a different page that is no longer in view.
    [ref, pathname],
  );
};

const determineSection = ({
  isMobile,
  navigationState,
  pathname,
}: {
  isMobile: boolean;
  navigationState: NavigationState;
  pathname: string;
}) => {
  if (
    isMobile &&
    (navigationState.state === 'top-level' ||
      (navigationState.state === 'default' && pathname === '/'))
  ) {
    return 'top-level';
  }

  if (
    navigationState.state === 'chose-section'
      ? navigationState.section === 'dashboard'
      : // TODO: Fix the /authorize route to either show a loading menu or use the redirect URL to determine the correct menu
        pathname.startsWith('/manage') || pathname === '/authorize'
  ) {
    return 'dashboard';
  }

  if (
    navigationState.state === 'chose-section'
      ? navigationState.section === 'schema'
      : pathname.startsWith('/schema')
  ) {
    return 'schema';
  }

  return 'docs';
};

export const Menu = ({
  close,
  isOpen,
  onForceOpen,
  usedHeight,
  breadcrumbs,
}: {
  close: () => void;
  isOpen: boolean;
  onForceOpen: () => void;
  usedHeight: string;
  breadcrumbs: Breadcrumb[] | null;
}) => {
  const scrollingParentRef = useRef<HTMLElement>(null);

  const { pathname } = useLocation();
  const { permissions } = usePermissions();
  const { isLoading, isAuthenticated } = useAuth0();
  const responsiveValue = useResponsiveValue();
  const ref = useRef<HTMLAnchorElement | null>(null);
  const [navigationState, setNavigationState] = useNavigationState(isOpen);

  useScrollRefIntoViewOnPathChange(ref, pathname);
  useOnEscape(close);

  const breakpoint =
    responsiveValue({ mobile: 0, tablet: 1, desktop: 2, wide: 3 }) ?? -1;

  const isMobile = breakpoint === 0;
  const isShownAsSidebar = breakpoint === 3;
  const isLoggedOut = !isLoading && !isAuthenticated;

  let content;
  switch (determineSection({ isMobile, navigationState, pathname })) {
    case 'top-level': {
      content = (
        <TopLevelNavigation
          onSelect={(section) =>
            setNavigationState({ state: 'chose-section', section })
          }
        />
      );
      break;
    }
    case 'dashboard':
      if (!isShownAsSidebar || isLoggedOut) {
        return null;
      }
      content = renderMenuSections(
        dashboardSite,
        pathname,
        ref,
        close,
        permissions,
      );
      break;
    case 'schema':
      content = (
        <SchemaMenu
          onClick={close}
          scrollingParentRef={scrollingParentRef}
          isOpen={isOpen}
        />
      );
      break;
    case 'docs':
      content = renderMenuSections(
        documentationSite,
        pathname,
        ref,
        close,
        permissions,
      );
      break;
  }

  return (
    <>
      <Hidden above="desktop">
        {isOpen && (
          <Box
            className={styles.overlay}
            onClick={close}
            zIndex="dropdownBackdrop"
          />
        )}
      </Hidden>

      <Box height="full" position="relative">
        <Box
          background="surface"
          className={styles.menu[`${isOpen}`]}
          component="nav"
          ref={scrollingParentRef}
          style={{ height: `calc(100vh - ${usedHeight})`, top: usedHeight }}
          zIndex="dropdown"
        >
          {isMobile ? (
            <Box paddingY="xsmall">
              <Stack space="small">
                <Box paddingX="small">
                  <Breadcrumbs breadcrumbs={breadcrumbs} visibility={isOpen} />
                </Box>

                {navigationState.state !== 'top-level' && (
                  <FunctionalMenuItem
                    onClick={() => setNavigationState({ state: 'top-level' })}
                  >
                    <IconArrow direction="left" /> Main menu
                  </FunctionalMenuItem>
                )}

                <Box paddingX="small">
                  <SiteSearch inline onForceOpen={onForceOpen} />
                </Box>
              </Stack>
            </Box>
          ) : null}

          <Box paddingY="medium" position="relative">
            <Stack space="medium">{content}</Stack>
          </Box>
        </Box>
      </Box>
    </>
  );
};
